import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Container, Form, InputGroup, Row, Spinner, Stack } from 'react-bootstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { formatDateDDMMYYYY } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { useUserFilter } from '../../apps/professional-app/fa/common/userFilter/hooks'
import { CreatorIcon } from '../../apps/professional-app/fa/PastMeetings/CreatorIcon'
import { SourceIcon } from '../../apps/professional-app/fa/PastMeetings/SourceIcon'
import { useGetRoomSessionsForFilterQuery } from '../../apps/professional-app/fa/store/fa'
import { getRoomTrackingDetails } from '../../apps/professional-app/fa/tracking'
import { IntegrationServiceId } from '../../common/integrations'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { useEffectivePlatform } from '../../common/useEffectivePlatform'
import { parseMetadata } from '../../common/util'
import { AutoJoinControl } from '../../components/AutoJoin/AutoJoinControl'
import { Button } from '../../components/Button/Button'
import { CustomPaginationComponent } from '../../components/DataTable/CustomPaginationComponent'
import { Divider } from '../../components/Divider/Divider'
import { PluginPromo, PromoType } from '../../components/Promo/Plugin/PluginPromo'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { AllFilterModal, AllFilterModalMode } from '../../components/SessionFilters/AllFilterModal'
import { FilterAttribute } from '../../components/SessionFilters/model'
import { SessionQuickFilter } from '../../components/SessionFilters/QuickFilters'
import { SessionContactFilter } from '../../components/SessionFilters/SessionContactFilter'
import { SessionDateFilter } from '../../components/SessionFilters/SessionDateFilter'
import { SessionMeetingTemplateFilter } from '../../components/SessionFilters/SessionMeetingTemplateFilter'
import { SessionUserFilter } from '../../components/SessionFilters/SessionUserFilter'
import { useSessionFilters } from '../../components/SessionFilters/useSessionFilters'
import { RoomSession } from '../../models/Room'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import {
  selectSessionFilter,
  selectSessionFilterDateRange,
  selectSessionFilterMeetingKind,
  selectSessionFilterMeetingSources,
  selectSessionFilterMeetingTemplateIds,
  selectSessionFilterTags,
  selectSessionFilterUserIds,
  selectUpcomingMeetingsCompositeFilter,
} from '../../store/sessionFilter/sessionFilter'
import { useGetMeQuery } from '../../store/user/user'
import { MeetingCreatorDisplay } from '../LiveMeetings/MeetingCreatorDisplay'
import { MeetingDateDisplay } from '../LiveMeetings/MeetingDateDisplay'
import { MeetingParticipantsDisplay } from '../LiveMeetings/MeetingParticipantsDisplay'
import { MeetingTitleDisplay } from '../LiveMeetings/MeetingTitleDisplay'
import { IncompleteBadge } from './IncompleteBadge'
import { MeetingTemplateSelector } from './MeetingTemplateSelector'
import { UpcomingHeaderRow } from './UpcomingHeaderRow'
import { PrivateBadge } from '../LiveMeetings/PrivateBadge'
import { ChangeMeetingOptions } from '../LiveMeetings/ChangeMeetingOptions'
import { IntegrationIcon } from '../../components/IntegrationIcon'

export const Upcoming: React.FC = () => {
  useDocumentTitle('Upcoming')

  return (
    <Container className='px-0'>
      <Stack gap={5}>
        <Row>
          <Col>
            <h3>Upcoming meetings</h3>
          </Col>
        </Row>
        <UpcomingHeaderRow />
        <UpcomingMeetingList />
      </Stack>
    </Container>
  )
}

const useNewIntegrationTimeout = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const param = 'isNewIntegration' as const

  // If new integration just added, delay query for 5 seconds to allow backend to sync data
  const [timeout, setTimeoutValue] = useState(() => {
    if (searchParams.has(param)) {
      return 5_000
    }
    return 0
  })

  useEffect(() => {
    if (timeout > 0) {
      const timer = setTimeout(() => {
        setSearchParams((searchParams) => {
          searchParams.delete(param)
          return searchParams
        })
        setTimeoutValue(0)
      }, timeout)
      return () => clearTimeout(timer)
    }
  }, [timeout, setSearchParams])

  return timeout
}

const UpcomingMeetingList = () => {
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { data: currentUser } = useGetMeQuery({ accountId })
  const defaultUserIds = useMemo(() => [currentUserId], [currentUserId])

  const [showAllFilterModal, setShowAllFilterModal] = useState<boolean>(false)
  const [allFilterModalMode, setAllFilterModalMode] = useState<AllFilterModalMode>(AllFilterModalMode.VIEW)
  const compositeFilter = useAppSelector(selectUpcomingMeetingsCompositeFilter)
  const sessionQuickFilter = useAppSelector(selectSessionFilter)
  const sessionFilterUserIds = useAppSelector(selectSessionFilterUserIds)
  const sessionFilterDateRange = useAppSelector(selectSessionFilterDateRange)
  const sessionFilterMeetingTypeIds = useAppSelector(selectSessionFilterMeetingTemplateIds)
  const sessionFilterMeetingKind = useAppSelector(selectSessionFilterMeetingKind)
  const sessionFilterMeetingSources = useAppSelector(selectSessionFilterMeetingSources)
  const sessionFilterTags = useAppSelector(selectSessionFilterTags)
  const filterCount =
    sessionFilterUserIds.length +
    (sessionFilterDateRange ? 1 : 0) +
    sessionFilterMeetingTypeIds.length +
    sessionFilterTags.length +
    (sessionFilterMeetingKind && !sessionFilterMeetingSources.length ? 1 : 0) +
    sessionFilterMeetingSources.length

  const { page, pageSize, totalElements, setTotalElements, handlePageChange, handlePageSizeChange, handleFilterChange, resetFilters } = useSessionFilters({
    defaultUserIds,
    currentUserId,
  })

  const timeout = useNewIntegrationTimeout()
  const newIntegrationBackoff = timeout > 0

  const query = useGetRoomSessionsForFilterQuery(
    {
      accountId,
      ...(sessionQuickFilter ? { filterId: sessionQuickFilter.id } : { filter: compositeFilter }),
      sort: { attr: FilterAttribute.STARTED, dir: 'asc' },
      pageRequest: { page: page - 1, size: pageSize },
    },
    { skip: currentUser == null || newIntegrationBackoff },
  )

  const roomSessions = query.data
  const isLoading = newIntegrationBackoff || query.isLoading || query.isFetching
  const refetchRoomSessions = query.refetch

  useEffect(() => {
    setTotalElements(roomSessions?.page.totalElements ?? 0)
  }, [roomSessions, setTotalElements])

  const [roomSessionTextFilter, setRoomSessionTextFilter] = useState<string>('')
  const filterRoomSessions = useCallback((sessions: RoomSession[], filterInput: string): RoomSession[] => {
    if (!filterInput) {
      return sessions
    }
    const filter = filterInput.toLowerCase()
    return sessions.filter((session) => {
      const { source, external: { title = null } = {} } = parseMetadata(session.metadata)
      const fields = [title, source]
      if (fields.some((field) => typeof field === 'string' && field.toLowerCase().includes(filter))) {
        return true
      }
      return !!session.users.filter((user) => {
        return (
          user.id?.toLowerCase().includes(filter) ||
          user.name?.toLowerCase().includes(filter) ||
          user.email?.toLowerCase().includes(filter) ||
          user.firstName?.toLowerCase().includes(filter) ||
          user.lastName?.toLowerCase().includes(filter) ||
          user.username?.toLowerCase().includes(filter) ||
          user.phoneNumber?.toLowerCase().includes(filter)
        )
      }).length
    })
  }, [])

  const onRoomSessionTextFilterChange = (filter: string) => {
    setRoomSessionTextFilter(filter)
  }

  const onHideAllFilterModal = () => {
    setShowAllFilterModal(false)
    setAllFilterModalMode(AllFilterModalMode.VIEW)
  }

  const handleOnEditClicked = () => {
    setAllFilterModalMode(AllFilterModalMode.EDIT)
    setShowAllFilterModal(true)
  }

  const handleNewFilterClicked = () => {
    resetFilters()
    onRoomSessionTextFilterChange('')
    setAllFilterModalMode(AllFilterModalMode.NEW)
    setShowAllFilterModal(true)
  }

  const handleClearFilters = () => {
    resetFilters()
    onRoomSessionTextFilterChange('')
  }

  const filteredRoomSessions = useMemo(
    () => (roomSessions ? filterRoomSessions(roomSessions.data, roomSessionTextFilter) : []),
    [roomSessions, filterRoomSessions, roomSessionTextFilter],
  )

  return (
    <Container className='p-0'>
      {showAllFilterModal ? (
        <AllFilterModal
          show={showAllFilterModal}
          onHide={onHideAllFilterModal}
          mode={allFilterModalMode}
          filterType='upcoming'
          onFilterChanged={handleFilterChange}
        />
      ) : null}
      <Stack gap={4}>
        <Row className='justify-content-between align-items-end align-self-stretch gap-1 m-0'>
          <Col className='d-none d-lg-block p-0'>
            <SessionUserFilter filterType='upcoming' disabled={isLoading} onFilterChanged={handleFilterChange} />
          </Col>
          <Col className='d-none d-lg-block p-0'>
            <SessionContactFilter filterType='upcoming' disabled={isLoading} onFilterChanged={handleFilterChange} />
          </Col>
          <Col className='d-none d-lg-block p-0'>
            <SessionDateFilter filterType='upcoming' disabled={isLoading} onFilterChanged={handleFilterChange} />
          </Col>
          <Col className='d-none d-lg-block p-0'>
            <SessionMeetingTemplateFilter filterType='upcoming' disabled={isLoading} onFilterChanged={handleFilterChange} />
          </Col>

          <Col xs={12} sm className='mw-0 p-0'>
            <SessionQuickFilter
              filterType='upcoming'
              disabled={isLoading}
              onEditClicked={handleOnEditClicked}
              onNewFilterClicked={handleNewFilterClicked}
              onFilterChanged={handleFilterChange}
            />
          </Col>

          <Col xs={12} sm='auto' className='mt-3 mt-sm-0 p-0'>
            <div className='d-flex align-items-center justify-content-end gap-1'>
              <div className='d-none d-lg-flex gap-1 cursor-pointer'>
                <i className='bi bi-arrow-clockwise' />
                <Button variant='link' className='button-link-medium' onClick={handleClearFilters}>
                  Clear all
                </Button>
              </div>
              <Button variant='darkGhost' className='d-none d-sm-flex p-2 paragraph-medium' onClick={() => setShowAllFilterModal(true)}>
                <i className='bi bi-funnel' />
                <div className='d-lg-none paragraph-small'>All filters</div>
                <div className='lead-small'>{filterCount}</div>
              </Button>
              <Button variant='darkGhost' className='d-flex d-sm-none p-2 w-100 paragraph-medium' onClick={() => setShowAllFilterModal(true)}>
                <i className='bi bi-funnel' />
                <div className='paragraph-small'>All filters</div>
                <div className='lead-small'>{filterCount}</div>
              </Button>
            </div>
          </Col>
        </Row>
        <Divider />
        {isLoading ? (
          <Row className='mt-3'>
            <Col className='d-flex justify-content-center'>
              <Spinner />
            </Col>
          </Row>
        ) : (
          <>
            <Row>
              <Col>
                <div>
                  {filteredRoomSessions.length} {`meeting${filteredRoomSessions.length !== 1 ? 's' : ''}`}
                </div>
              </Col>
              <Col xs={12} md='auto' className='m-0'>
                <InputGroup className='input-group-container gap-4'>
                  <Form.Control
                    type='text'
                    list='rooms'
                    value={roomSessionTextFilter}
                    onChange={(e) => onRoomSessionTextFilterChange(e.currentTarget.value)}
                    placeholder='Search in meetings'
                  />
                  <i className='px-2 bi bi-search'></i>
                </InputGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <RoomSessionList roomSessions={filteredRoomSessions} onSessionChange={refetchRoomSessions} />
              </Col>
            </Row>
            <Row className='justify-content-end'>
              <Col>
                <CustomPaginationComponent
                  rowsPerPage={pageSize}
                  rowCount={totalElements}
                  currentPage={page}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handlePageSizeChange}
                />
              </Col>
            </Row>
          </>
        )}
      </Stack>
    </Container>
  )
}

const RoomSessionList: React.FC<{
  roomSessions: RoomSession[]
  onSessionChange?: (roomSession: RoomSession) => void
}> = ({ roomSessions, onSessionChange }) => {
  const groupedRoomSessions = roomSessions.reduce(
    (
      acc: {
        [key: string]: RoomSession[]
      },
      roomSession,
    ) => {
      const date = formatDateDDMMYYYY(roomSession.startedAt)
      acc[date] = acc[date] || []
      acc[date].push(roomSession)
      return acc
    },
    {},
  )
  const today = formatDateDDMMYYYY(new Date().toDateString())

  return (
    <Container fluid className='p-0'>
      {Object.entries(groupedRoomSessions).map(([date, roomSessions], idx) => {
        return (
          <div className='zocks-mb-1.5' key={`session_${idx}`}>
            <div className='lead-medium'>{date === today ? 'Today' : date}</div>

            <Stack gap={2} className='p-3'>
              {roomSessions.map((roomSession, sub_idx) => {
                return (
                  <>
                    <RoomSessionListItem key={roomSession.id} session={roomSession} onSessionChange={onSessionChange} />
                    {idx === 0 && sub_idx === 0 ? <UpcomingMeetingPluginPromo /> : null}
                  </>
                )
              })}
            </Stack>
          </div>
        )
      })}
    </Container>
  )
}

const RoomSessionListItem: React.FC<{
  session: RoomSession
  onSessionChange?: (session: RoomSession) => void
}> = ({ session, onSessionChange }) => {
  const {
    source,
    service_id: serviceId,
    creator_service_id: creatorServiceId,
    external: { title = null, platform = null, userAgent = null } = {},
  } = parseMetadata(session.metadata)

  const effectivePlatform = useEffectivePlatform(source, platform, userAgent)
  const { tracked } = useTracking()
  const { userFilterValues } = useUserFilter('upcoming')
  const navigate = useNavigate()

  const _onMeetingPrepOpen = (roomId: string, sessionId: string) => {
    navigate({
      pathname: `/workspace/upcoming/room/${roomId}/session/${sessionId}/`,
    })
  }

  const onMeetingPrepOpen = tracked(_onMeetingPrepOpen, {
    eventName: TrackEvent.MEETING_PREP_OPENED,
    eventDetails: {
      ...getRoomTrackingDetails(session),
      source: session.isActive ? 'live' : 'upcoming',
    },
  })

  return (
    <Container fluid className='meeting-card'>
      <Row className='m-0'>
        <Col xs={12} lg className='px-0 meeting-info'>
          <Row className='d-flex gap-3 m-0'>
            <Col xs='auto' className='px-0'>
              <SourceIcon source={effectivePlatform ? `${source || serviceId}-${effectivePlatform}` : source || serviceId} />
            </Col>
            <Col className='d-flex flex-column justify-content-center overflow-hidden px-0 meeting-details'>
              <Row className='meeting-status-row m-0 align-items-center'>
                <Col xs='auto' className='p-0'>
                  {creatorServiceId && <CreatorIcon creator={creatorServiceId} />}
                </Col>
                <Col xs='auto' className='p-0'>
                  <MeetingDateDisplay startedAt={session.startedAt} finishedAt={session.finishedAt} />
                </Col>
                {session.isPrivate ? (
                  <Col xs='auto' className='p-0'>
                    <PrivateBadge />
                  </Col>
                ) : null}
                <Col xs='auto' className='p-0'>
                  <IncompleteBadge roomSession={session} />
                </Col>
              </Row>
              <Row>
                <Col>
                  <MeetingTitleDisplay title={title} />{' '}
                </Col>
              </Row>
              <Row>
                <Col xs='auto' className='paragraph-medium medium upcoming-created-by-name'>
                  <Stack direction='horizontal' gap={3}>
                    <MeetingCreatorDisplay creatorId={session.roomCreatedBy?.id} userLabels={userFilterValues} />
                    <MeetingParticipantsDisplay creatorId={session.roomCreatedBy?.id} users={session.users} />
                  </Stack>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>

        <Col lg={'auto'} className='gap-3 py-3 p-md-0 align-content-center'>
          <Stack direction='horizontal' gap={3}>
            <AutoJoinControl session={session} />
            <MeetingTemplateSelector session={session} onSessionChange={onSessionChange} />
            <ScopedBlock
              scope={{
                scope: 'ai_agent:execute',
                superScope: ['ai_agent:admin'],
              }}
              quota={{
                quota: 'ai.agent.value',
              }}
              missingScopeAction={MissingScopeAction.HIDE}
            >
              <Button variant='lightPurple' className='text-nowrap' onClick={() => onMeetingPrepOpen(session.roomId, session.id)}>
                Meeting prep
              </Button>
            </ScopedBlock>
            <ChangeMeetingOptions session={session} onSessionChange={onSessionChange} />
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}

const UpcomingMeetingPluginPromo: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser } = useGetMeQuery({ accountId })

  const hasOutlookIntegration = useMemo(() => {
    return !!currentUser?.integrations?.some((i) => i.serviceId === IntegrationServiceId.OUTLOOK_CALENDAR || i.serviceId === IntegrationServiceId.OUTLOOK_MAIL)
  }, [currentUser])

  if (!hasOutlookIntegration) {
    return null
  }

  return (
    <PluginPromo
      Icon={<IntegrationIcon serviceId={IntegrationServiceId.OUTLOOK_CALENDAR} className='outlook-logo' width={32} height={32} />}
      InstallButton={
        <a
          className='button-link-medium outlook-promo-button text-decoration-none p-2 zocks-border-0.25'
          href='https://appsource.microsoft.com/en-us/product/office/WA200008950'
          target='_blank'
          rel='noreferrer noopeener'
        >
          Install Outlook Add-in
        </a>
      }
      header='Bring Zocks to your Outlook'
      body='Access meeting prep, client details, and tasks directly in Outlook. Email and calendar features in one seamless experience.'
      containerClassname='outlook-promo-container'
      promoType={PromoType.OUTLOOK_CALENDAR}
    />
  )
}
